import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { notificationsAPI } from '../services/api';

const URL_RE = /(https?:\/\/[^\s]+)/g;

/** Splits plain text on bare URLs and renders them as clickable links — the rest stays plain text (React escapes it automatically). URL_RE has a capturing group, so split() puts matched URLs at odd indices. */
function linkifyText(text) {
  return text.split(URL_RE).map((part, i) =>
    i % 2 === 1
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-orange-600 underline break-words">{part}</a>
      : <span key={i}>{part}</span>
  );
}

export default function ReadMessage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    notificationsAPI.readMessage(token)
      .then((r) => setData(r))
      .catch((e) => setError(e.response?.data?.error || 'This link has expired or is invalid.'))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <main className="min-h-[100dvh] bg-[#f4f6fa] text-slate-900">
      <header className="bg-[#1b2857] px-4 pb-6 pt-[max(16px,env(safe-area-inset-top))] text-white">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white p-1 shadow">
            <img src="/awtf_logo.png" alt="AWTF" className="h-full w-full object-contain" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[.16em] text-orange-300">Advocates Welfare Trust Fund</p>
            <h1 className="truncate text-lg font-bold">AWTF Portal</h1>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-lg px-4 py-6">
        {loading && (
          <div className="flex items-center justify-center gap-3 py-16 text-sm text-slate-400">
            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            Loading message…
          </div>
        )}

        {!loading && error && (
          <div className="rounded-[24px] bg-white p-7 text-center shadow-sm ring-1 ring-slate-200">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100 text-2xl">⏱</div>
            <h2 className="mt-4 text-lg font-bold text-slate-800">This link is no longer available</h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">{error}</p>
          </div>
        )}

        {!loading && data && (
          <div className="rounded-[24px] bg-white p-6 shadow-sm ring-1 ring-slate-200 sm:p-7">
            <p className="text-[11px] font-bold uppercase tracking-[.16em] text-orange-500">Message from AWTF</p>
            {data.subject && (
              <h2 className="mt-1 text-xl font-bold leading-snug text-slate-900">{data.subject}</h2>
            )}
            <p className="mt-4 whitespace-pre-wrap text-[15px] leading-7 text-slate-700">{linkifyText(data.message)}</p>
            {data.created_at && (
              <p className="mt-6 border-t border-slate-100 pt-4 text-xs text-slate-400">
                Sent: {data.created_at.slice(0, 16).replace('T', ' ')}
              </p>
            )}
          </div>
        )}

        <p className="mt-6 text-center text-[11px] leading-5 text-slate-400">
          &copy; {new Date().getFullYear()} Advocates Welfare Trust Fund
        </p>
      </div>
    </main>
  );
}
